import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "../../lib/api";
import { ThumbsUp, ThumbsDown, Eye, CalendarPlus, Activity } from "lucide-react";

const PERIODS = [
  { v: "7", label: "7 jours" },
  { v: "30", label: "30 jours" },
  { v: "all", label: "Tout" },
];

const Bar = ({ value, max }) => (
  <div className="h-1 bg-[var(--line)] rounded-full mt-1.5 overflow-hidden">
    <div
      className="h-full bg-[var(--red)] rounded-full transition-[width] duration-500"
      style={{ width: `${max ? Math.max(4, (value / max) * 100) : 0}%` }}
    />
  </div>
);

const Total = ({ label, value, icon: Icon, testId }) => (
  <div data-testid={testId} className="bg-white border border-[var(--line)] rounded-md p-5">
    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.25em] text-neutral-500">
      {label} <Icon className="h-4 w-4 text-[var(--red)]" />
    </div>
    <div className="font-display text-4xl mt-3">{value ?? "—"}</div>
  </div>
);

const Panel = ({ title, testId, empty, rows, children }) => (
  <section data-testid={testId} className="bg-white border border-[var(--line)] rounded-md p-6">
    <h2 className="font-display text-xl mb-4">{title}</h2>
    {rows?.length ? (
      <ol className="divide-y divide-[var(--line)]">{children}</ol>
    ) : (
      <div className="rounded-md border border-dashed border-[var(--line-strong)] py-8 text-center text-sm text-neutral-500">
        {empty}
      </div>
    )}
  </section>
);

const Row = ({ rank, img, title, href, right, value, max, testId }) => (
  <li data-testid={testId} className="flex items-center gap-3 py-3">
    <span className="w-5 text-xs text-neutral-400 tabular-nums">{rank}</span>
    {img ? (
      <img src={img} alt="" className="h-12 w-9 object-cover rounded-sm bg-neutral-100 shrink-0" />
    ) : (
      <div className="h-12 w-9 rounded-sm bg-neutral-100 shrink-0" />
    )}
    <div className="min-w-0 flex-1">
      {href ? (
        <Link to={href} target="_blank" className="text-sm font-medium truncate block hover:text-[var(--red)]">
          {title}
        </Link>
      ) : (
        <span className="text-sm font-medium truncate block">{title}</span>
      )}
      <Bar value={value} max={max} />
    </div>
    <div className="text-xs text-neutral-600 text-right shrink-0 tabular-nums">{right}</div>
  </li>
);

export const AdminEngagement = () => {
  const [period, setPeriod] = useState("30");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/admin/engagement", { params: { period } }).then((r) => setData(r.data));
  }, [period]);

  const t = data?.totals;
  const maxVote = Math.max(0, ...(data?.top_votes || []).map((r) => r.up));
  const maxView = Math.max(0, ...(data?.top_articles || []).map((r) => r.views));
  const maxCal = Math.max(0, ...(data?.top_calendar || []).map((r) => r.clicks));

  return (
    <div className="space-y-8" data-testid="admin-engagement-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">Audience</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Engagement</h1>
          <p className="text-neutral-600 mt-2 text-sm">
            Compteurs internes anonymes, sans cookie. Vos propres visites (connecté) ne sont pas comptées.
          </p>
        </div>
        <div className="inline-flex border border-[var(--line-strong)] rounded-full p-1 bg-white" role="tablist">
          {PERIODS.map((p) => (
            <button
              key={p.v}
              type="button"
              data-testid={`engagement-period-${p.v}`}
              onClick={() => setPeriod(p.v)}
              className={`px-4 py-1.5 text-xs uppercase tracking-[0.2em] rounded-full transition-colors ${
                period === p.v ? "bg-black text-white" : "text-neutral-600 hover:text-black"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Total label="Votes" value={t?.votes} icon={ThumbsUp} testId="engagement-total-votes" />
        <Total label="Lectures d'articles" value={t?.article_views} icon={Eye} testId="engagement-total-views" />
        <Total label="Ajouts calendrier" value={t?.calendar_clicks} icon={CalendarPlus} testId="engagement-total-calendar" />
      </div>

      {!data ? (
        <div className="flex items-center gap-2 text-sm text-neutral-500" data-testid="engagement-loading">
          <Activity className="h-4 w-4 animate-pulse" /> Chargement…
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <Panel title="Titres les plus votés" testId="engagement-top-votes" rows={data.top_votes} empty="Aucun vote sur la période.">
            {data.top_votes.map((r, i) => (
              <Row
                key={r.release_id}
                testId={`engagement-vote-row-${i}`}
                rank={i + 1}
                img={r.poster_url}
                title={r.title}
                value={r.up}
                max={maxVote}
                right={
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center gap-0.5"><ThumbsUp className="h-3 w-3" />{r.up}</span>
                    <span className="inline-flex items-center gap-0.5 text-neutral-400"><ThumbsDown className="h-3 w-3" />{r.down}</span>
                  </span>
                }
              />
            ))}
          </Panel>
          <Panel title="Articles les plus lus" testId="engagement-top-articles" rows={data.top_articles} empty="Aucune lecture sur la période.">
            {data.top_articles.map((r, i) => (
              <Row
                key={r.slug}
                testId={`engagement-article-row-${i}`}
                rank={i + 1}
                img={r.cover_image_url ? mediaUrl(r.cover_image_url) : null}
                title={r.title}
                href={`/articles/${r.slug}`}
                value={r.views}
                max={maxView}
                right={`${r.views} lect.`}
              />
            ))}
          </Panel>
          <Panel title="Ajouts au calendrier" testId="engagement-top-calendar" rows={data.top_calendar} empty="Aucun ajout sur la période.">
            {data.top_calendar.map((r, i) => (
              <Row
                key={r.release_id}
                testId={`engagement-calendar-row-${i}`}
                rank={i + 1}
                img={r.poster_url}
                title={r.title}
                value={r.clicks}
                max={maxCal}
                right={<span>{r.clicks} <span className="text-neutral-400 block text-[10px]">Google {r.google} · Apple/Outlook {r.ics}</span></span>}
              />
            ))}
          </Panel>
        </div>
      )}
    </div>
  );
};
